import React, { useEffect, useMemo, useRef, useState } from 'react';
import { adminAPI } from '../services/api';

type FraudTab = 'blacklisted-payouts' | 'banned-merchants' | 'blacklisted-merchants';

interface BlacklistedPayoutEntry {
  id: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  ifscCode: string;
  ifscOnly: boolean;
  reason: string;
  createdAt: string; // ISO
}

interface BannedMerchantEntry {
  id: string;
  merchantId: string;
  merchantName: string;
  merchantEmail: string;
  merchantPhone?: string;
  reason: string;
  createdAt: string; // ISO
}

interface BlacklistedMerchantEntry {
  id: string;
  merchantId: string;
  merchantName: string;
  merchantEmail: string;
  merchantPhone?: string;
  reason: string;
  createdAt: string; // ISO
}

type RemoveTarget =
  | { type: 'blacklisted'; id: string; label: string }
  | { type: 'banned'; id: string; label: string }
  | { type: 'blacklisted-merchant'; id: string; label: string };

const STORAGE_KEYS = {
  bannedMerchants: 'admin_fraud_banned_merchants_v1',
  blacklistedMerchants: 'admin_fraud_blacklisted_merchants_v1',
} as const;

function safeJsonParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function normalizeIfsc(value: string): string {
  return (value || '').toUpperCase().replace(/\s+/g, '').trim();
}

function normalizeAccountNumber(value: string): string {
  return (value || '').replace(/\s+/g, '').trim();
}

function formatCompactDateTime(value: string): string {
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const FraudManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<FraudTab>('blacklisted-payouts');
  const [showAddBlacklistModal, setShowAddBlacklistModal] = useState(false);
  const [blacklistModalAttempted, setBlacklistModalAttempted] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);

  const [blacklisted, setBlacklisted] = useState<BlacklistedPayoutEntry[]>([]);
  const [bannedMerchants, setBannedMerchants] = useState<BannedMerchantEntry[]>(() =>
    safeJsonParse<BannedMerchantEntry[]>(
      typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEYS.bannedMerchants) : null,
      []
    )
  );
  const [blacklistedMerchants, setBlacklistedMerchants] = useState<BlacklistedMerchantEntry[]>(() =>
    safeJsonParse<BlacklistedMerchantEntry[]>(
      typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEYS.blacklistedMerchants) : null,
      []
    )
  );
  const syncedBannedMerchantIdsRef = useRef<Set<string>>(new Set());
  const syncedBlacklistedMerchantIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.bannedMerchants, JSON.stringify(bannedMerchants));
    } catch {
      /* ignore */
    }
  }, [bannedMerchants]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.blacklistedMerchants, JSON.stringify(blacklistedMerchants));
    } catch {
      /* ignore */
    }
  }, [blacklistedMerchants]);

  // Backfill old local-only fraud merchant lists to server enforcement flags.
  useEffect(() => {
    const bannedIdsToSync = bannedMerchants
      .map(entry => String(entry.merchantId || '').trim())
      .filter(id => /^\d+$/.test(id) && !syncedBannedMerchantIdsRef.current.has(id));

    const blacklistedIdsToSync = blacklistedMerchants
      .map(entry => String(entry.merchantId || '').trim())
      .filter(id => /^\d+$/.test(id) && !syncedBlacklistedMerchantIdsRef.current.has(id));

    if (bannedIdsToSync.length === 0 && blacklistedIdsToSync.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const merchantId of bannedIdsToSync) {
        try {
          await adminAPI.updateUser(Number(merchantId), { is_active: false });
          if (!cancelled) syncedBannedMerchantIdsRef.current.add(merchantId);
        } catch (error) {
          console.error(`Failed to sync banned merchant ${merchantId} to server:`, error);
        }
      }

      for (const merchantId of blacklistedIdsToSync) {
        try {
          await adminAPI.updateUser(Number(merchantId), { payout_blacklisted: true });
          if (!cancelled) syncedBlacklistedMerchantIdsRef.current.add(merchantId);
        } catch (error) {
          console.error(`Failed to sync blacklisted merchant ${merchantId} to server:`, error);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bannedMerchants, blacklistedMerchants]);

  const loadBlacklistedPayouts = async () => {
    try {
      const res = await adminAPI.listBlacklistedPayouts();
      const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
      const mapped: BlacklistedPayoutEntry[] = rows.map((r: any) => {
        const acct = String(r.account_number || '');
        const ifscOnly = Number(r.ifsc_only) === 1 || acct === '__IFSC_ONLY__';
        return {
          id: String(r.id),
          bankName: String(r.bank_name || ''),
          accountName: String(r.account_name || ''),
          accountNumber: acct,
          ifscCode: String(r.ifsc_code || ''),
          ifscOnly,
          reason: String(r.reason || ''),
          createdAt: r.created_at ? new Date(r.created_at).toISOString() : new Date().toISOString(),
        };
      });
      setBlacklisted(mapped);
    } catch (error) {
      console.error('Failed to load blacklisted payouts:', error);
    }
  };


  useEffect(() => {
    loadBlacklistedPayouts();
  }, []);

  useEffect(() => {
    const needsBackfill = blacklistedMerchants.some(
      entry => !entry.merchantPhone && entry.merchantId && /^\d+$/.test(entry.merchantId.trim())
    );
    if (!needsBackfill) return;

    let cancelled = false;
    (async () => {
      const updates = await Promise.all(
        blacklistedMerchants.map(async entry => {
          if (entry.merchantPhone || !entry.merchantId || !/^\d+$/.test(entry.merchantId.trim())) {
            return null;
          }
          try {
            const userId = Number(entry.merchantId);
            const resp = await adminAPI.getUserById(userId);
            const phone = resp?.data?.data?.user?.phone;
            if (!phone) return null;
            return { id: entry.id, phone: String(phone) };
          } catch {
            return null;
          }
        })
      );

      if (cancelled) return;
      const phoneById = new Map(
        updates.filter(Boolean).map(item => [item!.id, item!.phone])
      );
      if (phoneById.size === 0) return;

      setBlacklistedMerchants(prev =>
        prev.map(entry =>
          phoneById.has(entry.id) ? { ...entry, merchantPhone: phoneById.get(entry.id)! } : entry
        )
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [blacklistedMerchants]);

  const [blacklistForm, setBlacklistForm] = useState({
    bankName: '',
    accountName: '',
    accountNumber: '',
    ifscCode: '',
    reason: '',
    ifscOnly: false,
  });
  const [bankNameLookupLoading, setBankNameLookupLoading] = useState(false);

  const [blacklistedMerchantFilters, setBlacklistedMerchantFilters] = useState({
    merchantId: '',
    merchantEmail: '',
    merchantMobile: '',
  });
  const [bannedMerchantFilters, setBannedMerchantFilters] = useState({
    merchantId: '',
    merchantEmail: '',
    merchantMobile: '',
  });

  const blacklistErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    const accNo = normalizeAccountNumber(blacklistForm.accountNumber);
    const ifsc = normalizeIfsc(blacklistForm.ifscCode);

    if (!blacklistForm.bankName.trim()) errs.bankName = 'Bank name is required';
    if (!ifsc) errs.ifscCode = 'IFSC is required';
    if (ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) errs.ifscCode = 'Enter a valid IFSC';

    if (!blacklistForm.ifscOnly) {
      if (!blacklistForm.accountName.trim()) errs.accountName = 'Account name is required';
      if (!accNo) errs.accountNumber = 'Account number is required';
      if (accNo && !/^\d{6,20}$/.test(accNo)) errs.accountNumber = 'Enter a valid account number';
    }
    return errs;
  }, [blacklistForm]);


  const filteredBlacklistedMerchants = useMemo(() => {
    const normalize = (value: unknown) => String(value ?? '').trim().toLowerCase();
    const idQuery = blacklistedMerchantFilters.merchantId.trim().toLowerCase();
    const emailQuery = blacklistedMerchantFilters.merchantEmail.trim().toLowerCase();
    const mobileQuery = blacklistedMerchantFilters.merchantMobile.trim().toLowerCase();

    return blacklistedMerchants.filter(entry => {
      const idMatch = !idQuery || normalize(entry.merchantId).includes(idQuery);
      const emailMatch = !emailQuery || normalize(entry.merchantEmail).includes(emailQuery);
      const mobileMatch = !mobileQuery || normalize(entry.merchantPhone).includes(mobileQuery);
      return idMatch && emailMatch && mobileMatch;
    });
  }, [blacklistedMerchants, blacklistedMerchantFilters]);


  const filteredBannedMerchants = useMemo(() => {
    const normalize = (value: unknown) => String(value ?? '').trim().toLowerCase();
    const idQuery = bannedMerchantFilters.merchantId.trim().toLowerCase();
    const emailQuery = bannedMerchantFilters.merchantEmail.trim().toLowerCase();
    const mobileQuery = bannedMerchantFilters.merchantMobile.trim().toLowerCase();

    return bannedMerchants.filter(entry => {
      const idMatch = !idQuery || normalize(entry.merchantId).includes(idQuery);
      const emailMatch = !emailQuery || normalize(entry.merchantEmail).includes(emailQuery);
      const mobileMatch = !mobileQuery || normalize(entry.merchantPhone).includes(mobileQuery);
      return idMatch && emailMatch && mobileMatch;
    });
  }, [bannedMerchants, bannedMerchantFilters]);

  const visibleBlacklistModalErrors = useMemo(() => {
    if (!blacklistModalAttempted) return {} as Record<string, string>;
    return blacklistErrors;
  }, [blacklistModalAttempted, blacklistErrors]);

  useEffect(() => {
    if (showAddBlacklistModal) {
      setBlacklistModalAttempted(false);
    }
  }, [showAddBlacklistModal]);

  const fetchBankNameFromIfsc = async (ifscCode: string) => {
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode)) return;
    try {
      setBankNameLookupLoading(true);
      const response = await fetch(`https://ifsc.razorpay.com/${ifscCode}`);
      if (!response.ok) return;
      const bankData = await response.json();
      if (bankData?.BANK) {
        setBlacklistForm(prev => ({ ...prev, bankName: String(bankData.BANK) }));
      }
    } catch {
      // ignore IFSC lookup failures; user can still type bank name manually if needed
    } finally {
      setBankNameLookupLoading(false);
    }
  };

  const addBlacklistedPayout = () => {
    setBlacklistModalAttempted(true);
    if (Object.keys(blacklistErrors).length > 0) return;

    const accountNumber = normalizeAccountNumber(blacklistForm.accountNumber);
    const ifscCode = normalizeIfsc(blacklistForm.ifscCode);
    const accountName = blacklistForm.accountName.trim();
    const bankName = blacklistForm.bankName.trim();

    const payload = blacklistForm.ifscOnly
      ? {
          bank_name: bankName,
          ifsc_code: ifscCode,
          reason: blacklistForm.reason.trim(),
          ifsc_only: true as const,
        }
      : {
          account_name: accountName,
          bank_name: bankName,
          account_number: accountNumber,
          ifsc_code: ifscCode,
          reason: blacklistForm.reason.trim(),
        };

    adminAPI
      .addBlacklistedPayout(payload)
      .then(async () => {
        await loadBlacklistedPayouts();
        setBlacklistForm({ bankName: '', accountName: '', accountNumber: '', ifscCode: '', reason: '', ifscOnly: false });
        setBlacklistModalAttempted(false);
        setShowAddBlacklistModal(false);
      })
      .catch((error: any) => {
        console.error('Failed to add blacklisted payout:', error);
        const msg = error?.response?.data?.message || 'Failed to add blacklist entry';
        window.alert(msg);
      });
  };

  const removeBlacklistedPayout = (id: string) => {
    adminAPI
      .removeBlacklistedPayout(id)
      .then(async () => {
        await loadBlacklistedPayouts();
      })
      .catch((error: any) => {
        console.error('Failed to remove blacklisted payout:', error);
        const msg = error?.response?.data?.message || 'Failed to remove blacklist entry';
        window.alert(msg);
      });
  };

  const removeBannedMerchant = (id: string) => {
    setBannedMerchants(prev => prev.filter(e => e.id !== id));
  };

  const removeBlacklistedMerchant = (id: string) => {
    setBlacklistedMerchants(prev => prev.filter(e => e.id !== id));
  };

  const requestRemoveBlacklistedPayout = (entry: BlacklistedPayoutEntry) => {
    setRemoveTarget({
      type: 'blacklisted',
      id: entry.id,
      label: entry.ifscOnly ? `IFSC ${entry.ifscCode} (all accounts)` : `${entry.accountName} (${entry.accountNumber})`,
    });
  };

  const requestRemoveBannedMerchant = (entry: BannedMerchantEntry) => {
    setRemoveTarget({
      type: 'banned',
      id: entry.id,
      label: `${entry.merchantName} (${entry.merchantId})`,
    });
  };

  const requestRemoveBlacklistedMerchant = (entry: BlacklistedMerchantEntry) => {
    setRemoveTarget({
      type: 'blacklisted-merchant',
      id: entry.id,
      label: `${entry.merchantName} (${entry.merchantId})`,
    });
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    try {
      if (removeTarget.type === 'blacklisted') {
        removeBlacklistedPayout(removeTarget.id);
      } else if (removeTarget.type === 'banned') {
        const entry = bannedMerchants.find(e => e.id === removeTarget.id);
        if (entry?.merchantId) {
          await adminAPI.updateUser(Number(entry.merchantId), { is_active: true });
        }
        removeBannedMerchant(removeTarget.id);
      } else {
        const entry = blacklistedMerchants.find(e => e.id === removeTarget.id);
        if (entry?.merchantId) {
          await adminAPI.updateUser(Number(entry.merchantId), { payout_blacklisted: false });
        }
        removeBlacklistedMerchant(removeTarget.id);
      }
      setRemoveTarget(null);
    } catch (error: any) {
      const msg = error?.response?.data?.message || 'Failed to update merchant on server.';
      window.alert(msg);
    }
  };

  const blacklistHeaderCellStyle: React.CSSProperties = {
    padding: '10px 12px',
    fontSize: 13,
    lineHeight: 1.2,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  };

  const blacklistBodyCellStyle: React.CSSProperties = {
    padding: '8px 12px',
    fontSize: 14,
    lineHeight: 1.3,
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  };

  const compactFraudTableStyle: React.CSSProperties = {
    width: '100%',
    minWidth: 720,
    tableLayout: 'fixed',
  };

  const compactFraudTableWrapStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '100%',
    marginLeft: 0,
    marginRight: 0,
  };

  const blacklistActionRowStyle: React.CSSProperties = {
    marginTop: 14,
    marginBottom: 12,
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    maxWidth: 920,
    marginLeft: 'auto',
    marginRight: 'auto',
  };

  const addBlacklistButtonStyle: React.CSSProperties = {
    minWidth: 210,
    height: 40,
    padding: '0 18px',
    borderRadius: 10,
    border: '1px solid rgba(255, 255, 255, 0.35)',
    background: 'linear-gradient(135deg, #a78bfa, #8b5cf6)',
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: '0.2px',
    boxShadow: '0 8px 20px rgba(139, 92, 246, 0.35)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  };

  return (
    <div className="report-management">
      <div className="page-header">
        <div className="header-content">
          <h1>🕵️ Fraud Management</h1>
          <p>Blacklist payout bank details and ban merchants</p>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'blacklisted-payouts' ? 'active' : ''}`}
          onClick={() => setActiveTab('blacklisted-payouts')}
          type="button"
        >
          <span className="tab-icon">⛔</span>
          <span className="tab-label">Blacklisted Payouts</span>
          <span className="tab-count">{blacklisted.length}</span>
        </button>
        <button
          className={`tab ${activeTab === 'banned-merchants' ? 'active' : ''}`}
          onClick={() => setActiveTab('banned-merchants')}
          type="button"
        >
          <span className="tab-icon">🚫</span>
          <span className="tab-label">Banned Merchants</span>
          <span className="tab-count">{bannedMerchants.length}</span>
        </button>
        <button
          className={`tab ${activeTab === 'blacklisted-merchants' ? 'active' : ''}`}
          onClick={() => setActiveTab('blacklisted-merchants')}
          type="button"
        >
          <span className="tab-icon">⛔</span>
          <span className="tab-label">Blacklisted Merchants</span>
          <span className="tab-count">{blacklistedMerchants.length}</span>
        </button>
      </div>

      {activeTab === 'blacklisted-payouts' && (
        <div style={{ marginTop: 16 }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 6, color: 'rgba(255, 255, 255, 0.9)' }}>
            Blacklisted Payouts
          </h2>
          <p style={{ fontSize: '0.95rem', color: 'rgba(255, 255, 255, 0.8)', marginTop: 0 }}>
            Block a specific <b>account number + IFSC</b>, or block an entire <b>IFSC</b> so no payout can go to that branch.
          </p>

          <div style={blacklistActionRowStyle}>
            <button
              type="button"
              className="filter-button"
              onClick={() => setShowAddBlacklistModal(true)}
              style={addBlacklistButtonStyle}
            >
              ⛔ Add Blacklist Entry
            </button>
          </div>

          <div className="table-container" style={compactFraudTableWrapStyle}>
            <div className="filters-section" style={{ marginBottom: 0, borderRadius: 0 }}>
              <div className="filters-row" style={{ gridTemplateColumns: 'repeat(12, 1fr)' } as any}>
                <div className="filter-group" style={{ gridColumn: 'span 4' }}>
                  <label>Search Merchant ID</label>
                  <input
                    className="filter-select"
                    placeholder="Enter merchant ID"
                    value={blacklistedMerchantFilters.merchantId}
                    onChange={e => setBlacklistedMerchantFilters(prev => ({ ...prev, merchantId: e.target.value }))}
                  />
                </div>
                <div className="filter-group" style={{ gridColumn: 'span 4' }}>
                  <label>Search Merchant Email</label>
                  <input
                    className="filter-select"
                    placeholder="Enter merchant email"
                    value={blacklistedMerchantFilters.merchantEmail}
                    onChange={e => setBlacklistedMerchantFilters(prev => ({ ...prev, merchantEmail: e.target.value }))}
                  />
                </div>
                <div className="filter-group" style={{ gridColumn: 'span 4' }}>
                  <label>Search Merchant Mobile</label>
                  <input
                    className="filter-select"
                    placeholder="Enter mobile number"
                    value={blacklistedMerchantFilters.merchantMobile}
                    onChange={e => setBlacklistedMerchantFilters(prev => ({ ...prev, merchantMobile: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <table className="transaction-table merchants-table" style={compactFraudTableStyle}>
              <colgroup>
                <col style={{ width: '14%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={blacklistHeaderCellStyle}>Bank Name</th>
                  <th style={blacklistHeaderCellStyle}>Account Name</th>
                  <th style={blacklistHeaderCellStyle}>Account No</th>
                  <th style={blacklistHeaderCellStyle}>IFSC</th>
                  <th style={blacklistHeaderCellStyle}>Reason</th>
                  <th style={blacklistHeaderCellStyle}>Added</th>
                  <th style={blacklistHeaderCellStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {blacklisted.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '14px 12px', fontSize: 14, color: '#64748b' }}>
                      No blacklisted payouts yet.
                    </td>
                  </tr>
                ) : (
                  blacklisted.map(row => (
                    <tr key={row.id}>
                      <td style={blacklistBodyCellStyle}>{row.bankName || '—'}</td>
                      <td style={blacklistBodyCellStyle}>{row.ifscOnly ? 'All accounts' : row.accountName}</td>
                      <td
                        style={{
                          ...blacklistBodyCellStyle,
                          fontFamily: row.ifscOnly
                            ? undefined
                            : 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        }}
                      >
                        {row.ifscOnly ? '—' : row.accountNumber}
                      </td>
                      <td style={{ ...blacklistBodyCellStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                        {row.ifscCode}
                      </td>
                      <td style={{ ...blacklistBodyCellStyle, whiteSpace: 'normal' }}>{row.reason || '—'}</td>
                      <td style={blacklistBodyCellStyle}>{formatCompactDateTime(row.createdAt)}</td>
                      <td style={blacklistBodyCellStyle}>
                        <button
                          type="button"
                          className="filter-button"
                          onClick={() => requestRemoveBlacklistedPayout(row)}
                          style={{
                            minWidth: 84,
                            height: 30,
                            padding: '0 12px',
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                            border: '1px solid rgba(255, 255, 255, 0.25)',
                            color: '#fff',
                          }}
                        >
                          Unblacklist
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAddBlacklistModal && (
        <div
          onClick={() => setShowAddBlacklistModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(2, 6, 23, 0.75)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            overflowY: 'auto',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 720,
              margin: 'auto',
              borderRadius: 18,
              border: '2px solid rgba(167, 139, 250, 0.75)',
              background: '#ffffff',
              boxShadow: '0 24px 60px rgba(76, 29, 149, 0.28)',
              color: '#1f2937',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '20px 22px 18px',
                borderBottom: '1px solid rgba(196, 181, 253, 0.65)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 16,
                background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', color: '#ffffff' }}>Add Blacklist Entry</h3>
                <p style={{ margin: '6px 0 0 0', fontSize: 12, lineHeight: 1.45, color: 'rgba(245, 243, 255, 0.92)' }}>
                  {blacklistForm.ifscOnly
                    ? 'Bank name is for your records. All payouts to this IFSC are blocked.'
                    : 'Bank name is for your records. Blocks when account number + IFSC match.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAddBlacklistModal(false)}
                style={{
                  flexShrink: 0,
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'rgba(255, 255, 255, 0.16)',
                  border: '1px solid rgba(255, 255, 255, 0.28)',
                  color: '#ffffff',
                  fontSize: 22,
                  lineHeight: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div style={{ padding: '18px 22px 10px', background: '#ffffff' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                  columnGap: 20,
                  rowGap: 12,
                  alignItems: 'start',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    cursor: 'pointer',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: '2px solid rgba(196, 181, 253, 0.8)',
                    background: blacklistForm.ifscOnly ? 'rgba(139, 92, 246, 0.08)' : '#fafafa',
                    gridColumn: '1 / -1',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={blacklistForm.ifscOnly}
                    onChange={e => setBlacklistForm(prev => ({ ...prev, ifscOnly: e.target.checked }))}
                    style={{ width: 17, height: 17, marginTop: 2, flexShrink: 0, accentColor: '#8b5cf6' }}
                  />
                  <span style={{ fontSize: 13, lineHeight: 1.35, color: '#1f2937', minWidth: 0 }}>
                    <span style={{ fontWeight: 800 }}>Blacklist IFSC only</span>
                    <span style={{ fontWeight: 500, opacity: 0.82, marginLeft: 6 }}>
                      — any account at this branch; account fields optional.
                    </span>
                  </span>
                </label>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                  <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: '#1f2937' }}>
                    ACCOUNT NUMBER
                  </label>
                  <input
                    className="filter-select"
                    placeholder={blacklistForm.ifscOnly ? 'Not used (IFSC-only)' : 'Digits only, no spaces'}
                    inputMode="numeric"
                    autoComplete="off"
                    readOnly={blacklistForm.ifscOnly}
                    value={blacklistForm.accountNumber}
                    onChange={e => setBlacklistForm(prev => ({ ...prev, accountNumber: e.target.value.replace(/\D/g, '') }))}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: visibleBlacklistModalErrors.accountNumber
                        ? '1px solid rgba(239, 68, 68, 0.7)'
                        : '2px solid rgba(196, 181, 253, 0.8)',
                      background: blacklistForm.ifscOnly ? '#f1f5f9' : '#ffffff',
                      color: '#0f172a',
                      fontSize: 14,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      opacity: blacklistForm.ifscOnly ? 0.75 : 1,
                    }}
                  />
                  <div style={{ minHeight: 18, fontSize: 12, lineHeight: 1.35, color: '#fecaca' }}>
                    {visibleBlacklistModalErrors.accountNumber || '\u00a0'}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                  <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: '#1f2937' }}>
                    ACCOUNT NAME
                  </label>
                  <input
                    className="filter-select"
                    placeholder={blacklistForm.ifscOnly ? 'Not used (IFSC-only)' : 'As per bank records'}
                    autoComplete="off"
                    readOnly={blacklistForm.ifscOnly}
                    value={blacklistForm.accountName}
                    onChange={e => setBlacklistForm(prev => ({ ...prev, accountName: e.target.value }))}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: visibleBlacklistModalErrors.accountName
                        ? '1px solid rgba(239, 68, 68, 0.7)'
                        : '2px solid rgba(196, 181, 253, 0.8)',
                      background: blacklistForm.ifscOnly ? '#f1f5f9' : '#ffffff',
                      color: '#0f172a',
                      fontSize: 14,
                      opacity: blacklistForm.ifscOnly ? 0.75 : 1,
                    }}
                  />
                  <div style={{ minHeight: 18, fontSize: 12, lineHeight: 1.35, color: '#fecaca' }}>
                    {visibleBlacklistModalErrors.accountName || '\u00a0'}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                  <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: '#1f2937' }}>
                    IFSC CODE
                  </label>
                  <input
                    className="filter-select"
                    placeholder="e.g. SBIN0001234"
                    autoComplete="off"
                    value={blacklistForm.ifscCode}
                    onChange={e => {
                      const nextIfsc = e.target.value.toUpperCase().replace(/\s/g, '');
                      setBlacklistForm(prev => ({
                        ...prev,
                        ifscCode: nextIfsc,
                        bankName: nextIfsc.length < 11 ? '' : prev.bankName,
                      }));
                      if (nextIfsc.length === 11) {
                        fetchBankNameFromIfsc(nextIfsc);
                      }
                    }}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: visibleBlacklistModalErrors.ifscCode
                        ? '1px solid rgba(239, 68, 68, 0.7)'
                        : '2px solid rgba(196, 181, 253, 0.8)',
                      background: '#ffffff',
                      color: '#0f172a',
                      fontSize: 14,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      letterSpacing: '0.04em',
                    }}
                  />
                  <div style={{ minHeight: 18, fontSize: 12, lineHeight: 1.35, color: '#fecaca' }}>
                    {visibleBlacklistModalErrors.ifscCode || '\u00a0'}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                  <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: '#1f2937' }}>
                    BANK NAME
                  </label>
                  <input
                    className="filter-select"
                    placeholder={bankNameLookupLoading ? 'Fetching bank name from IFSC...' : 'Auto-filled from IFSC'}
                    autoComplete="off"
                    value={blacklistForm.bankName}
                    onChange={e => setBlacklistForm(prev => ({ ...prev, bankName: e.target.value }))}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: visibleBlacklistModalErrors.bankName
                        ? '1px solid rgba(239, 68, 68, 0.7)'
                        : '2px solid rgba(196, 181, 253, 0.8)',
                      background: '#ffffff',
                      color: '#0f172a',
                      fontSize: 14,
                    }}
                  />
                  <div style={{ minHeight: 18, fontSize: 12, lineHeight: 1.35, color: '#fecaca' }}>
                    {visibleBlacklistModalErrors.bankName || '\u00a0'}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: '1 / -1', minWidth: 0 }}>
                  <label style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: '#374151' }}>
                    REASON <span style={{ fontWeight: 500, opacity: 0.85 }}>(optional)</span>
                  </label>
                  <input
                    className="filter-select"
                    placeholder="Internal note — visible to admins only"
                    value={blacklistForm.reason}
                    onChange={e => setBlacklistForm(prev => ({ ...prev, reason: e.target.value }))}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: '2px solid rgba(196, 181, 253, 0.8)',
                      background: '#ffffff',
                      color: '#0f172a',
                      fontSize: 14,
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                padding: '16px 22px 22px',
                borderTop: '1px solid rgba(196, 181, 253, 0.55)',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                gap: 12,
                background: '#f8fafc',
              }}
            >
              <button
                type="button"
                className="action-button"
                onClick={() => setShowAddBlacklistModal(false)}
                style={{
                  padding: '10px 18px',
                  borderRadius: 12,
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #e2e8f0, #cbd5e1)',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="filter-button"
                onClick={addBlacklistedPayout}
                style={{
                  padding: '10px 20px',
                  borderRadius: 12,
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  color: '#ffffff',
                  boxShadow: '0 10px 20px rgba(139, 92, 246, 0.28)',
                }}
              >
                Add Blacklist Entry
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'banned-merchants' && (
        <div style={{ marginTop: 16 }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 6, color: 'rgba(255, 255, 255, 0.9)' }}>
            Banned Merchants
          </h2>
          <p style={{ fontSize: '0.95rem', color: 'rgba(255, 255, 255, 0.8)', marginTop: 0 }}>
            Banned merchants cannot log in (account deactivated). Unban reactivates the account.
          </p>

          <div className="filters-section" style={{ marginTop: 12 }}>
            <div className="filters-row" style={{ gridTemplateColumns: 'repeat(12, 1fr)' } as any}>
              <div className="filter-group" style={{ gridColumn: 'span 4' }}>
                <label>Search Merchant ID</label>
                <input
                  className="filter-select"
                  placeholder="Enter merchant ID"
                  value={bannedMerchantFilters.merchantId}
                  onChange={e => setBannedMerchantFilters(prev => ({ ...prev, merchantId: e.target.value }))}
                />
              </div>
              <div className="filter-group" style={{ gridColumn: 'span 4' }}>
                <label>Search Merchant Email</label>
                <input
                  className="filter-select"
                  placeholder="Enter merchant email"
                  value={bannedMerchantFilters.merchantEmail}
                  onChange={e => setBannedMerchantFilters(prev => ({ ...prev, merchantEmail: e.target.value }))}
                />
              </div>
              <div className="filter-group" style={{ gridColumn: 'span 4' }}>
                <label>Search Merchant Mobile</label>
                <input
                  className="filter-select"
                  placeholder="Enter mobile number"
                  value={bannedMerchantFilters.merchantMobile}
                  onChange={e => setBannedMerchantFilters(prev => ({ ...prev, merchantMobile: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className="table-container" style={compactFraudTableWrapStyle}>
            <table className="transaction-table merchants-table" style={compactFraudTableStyle}>
              <colgroup>
                <col style={{ width: '10%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '17%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '8%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={blacklistHeaderCellStyle}>Merchant ID</th>
                  <th style={blacklistHeaderCellStyle}>Name</th>
                  <th style={blacklistHeaderCellStyle}>Email</th>
                  <th style={blacklistHeaderCellStyle}>Mobile</th>
                  <th style={blacklistHeaderCellStyle}>Reason</th>
                  <th style={blacklistHeaderCellStyle}>Banned At</th>
                  <th style={blacklistHeaderCellStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredBannedMerchants.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '14px 12px', fontSize: 14, color: '#64748b' }}>
                      No matching banned merchants found.
                    </td>
                  </tr>
                ) : (
                  filteredBannedMerchants.map(row => (
                    <tr key={row.id}>
                      <td style={{ ...blacklistBodyCellStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                        {row.merchantId}
                      </td>
                      <td style={blacklistBodyCellStyle}>{row.merchantName}</td>
                      <td style={blacklistBodyCellStyle}>{row.merchantEmail || '-'}</td>
                      <td style={blacklistBodyCellStyle}>{row.merchantPhone || '-'}</td>
                      <td style={{ ...blacklistBodyCellStyle, whiteSpace: 'normal' }}>{row.reason || '—'}</td>
                      <td style={blacklistBodyCellStyle}>{formatCompactDateTime(row.createdAt)}</td>
                      <td style={blacklistBodyCellStyle}>
                        <button
                          type="button"
                          className="filter-button"
                          onClick={() => requestRemoveBannedMerchant(row)}
                          style={{
                            minWidth: 84,
                            height: 30,
                            padding: '0 12px',
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                            border: '1px solid rgba(255, 255, 255, 0.25)',
                            color: '#fff',
                          }}
                        >
                          Unban
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'blacklisted-merchants' && (
        <div style={{ marginTop: 16 }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 6, color: 'rgba(255, 255, 255, 0.9)' }}>
            Blacklisted Merchants
          </h2>
          <p style={{ fontSize: '0.95rem', color: 'rgba(255, 255, 255, 0.8)', marginTop: 0 }}>
            These merchants can log in but cannot create new payouts until unblacklisted (also enforced on the server).
          </p>

          <div className="filters-section" style={{ marginTop: 12 }}>
            <div className="filters-row" style={{ gridTemplateColumns: 'repeat(12, 1fr)' } as any}>
              <div className="filter-group" style={{ gridColumn: 'span 4' }}>
                <label>Search Merchant ID</label>
                <input
                  className="filter-select"
                  placeholder="Enter merchant ID"
                  value={blacklistedMerchantFilters.merchantId}
                  onChange={e => setBlacklistedMerchantFilters(prev => ({ ...prev, merchantId: e.target.value }))}
                />
              </div>
              <div className="filter-group" style={{ gridColumn: 'span 4' }}>
                <label>Search Merchant Email</label>
                <input
                  className="filter-select"
                  placeholder="Enter merchant email"
                  value={blacklistedMerchantFilters.merchantEmail}
                  onChange={e => setBlacklistedMerchantFilters(prev => ({ ...prev, merchantEmail: e.target.value }))}
                />
              </div>
              <div className="filter-group" style={{ gridColumn: 'span 4' }}>
                <label>Search Merchant Mobile</label>
                <input
                  className="filter-select"
                  placeholder="Enter mobile number"
                  value={blacklistedMerchantFilters.merchantMobile}
                  onChange={e => setBlacklistedMerchantFilters(prev => ({ ...prev, merchantMobile: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className="table-container" style={compactFraudTableWrapStyle}>
            <table className="transaction-table merchants-table" style={compactFraudTableStyle}>
              <colgroup>
                <col style={{ width: '10%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '17%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '8%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={blacklistHeaderCellStyle}>Merchant ID</th>
                  <th style={blacklistHeaderCellStyle}>Name</th>
                  <th style={blacklistHeaderCellStyle}>Email</th>
                  <th style={blacklistHeaderCellStyle}>Mobile</th>
                  <th style={blacklistHeaderCellStyle}>Reason</th>
                  <th style={blacklistHeaderCellStyle}>Blacklisted At</th>
                  <th style={blacklistHeaderCellStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredBlacklistedMerchants.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '14px 12px', fontSize: 14, color: '#64748b' }}>
                      No matching blacklisted merchants found.
                    </td>
                  </tr>
                ) : (
                  filteredBlacklistedMerchants.map(row => (
                    <tr key={row.id}>
                      <td style={{ ...blacklistBodyCellStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                        {row.merchantId}
                      </td>
                      <td style={blacklistBodyCellStyle}>{row.merchantName}</td>
                      <td style={blacklistBodyCellStyle}>{row.merchantEmail || '-'}</td>
                      <td style={blacklistBodyCellStyle}>{row.merchantPhone || '-'}</td>
                      <td style={{ ...blacklistBodyCellStyle, whiteSpace: 'normal' }}>{row.reason || '—'}</td>
                      <td style={blacklistBodyCellStyle}>{formatCompactDateTime(row.createdAt)}</td>
                      <td style={blacklistBodyCellStyle}>
                        <button
                          type="button"
                          className="filter-button"
                          onClick={() => requestRemoveBlacklistedMerchant(row)}
                          style={{
                            minWidth: 84,
                            height: 30,
                            padding: '0 12px',
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                            border: '1px solid rgba(255, 255, 255, 0.25)',
                            color: '#fff',
                          }}
                        >
                          Unblacklist
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {removeTarget && (
        <div
          onClick={() => setRemoveTarget(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(2, 6, 23, 0.72)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 700,
              borderRadius: 18,
              border: '2px solid #9d8cf5',
              background: '#f8f8fb',
              boxShadow: '0 28px 60px rgba(15, 23, 42, 0.45)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '20px 26px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                borderBottom: '1px solid #c4b5fd',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h3 style={{ margin: 0, fontSize: 22, color: '#ffffff', fontWeight: 800, letterSpacing: '0.2px' }}>
                {removeTarget.type === 'blacklisted'
                  ? 'Remove Blacklist Entry'
                  : removeTarget.type === 'banned'
                    ? 'Unban Merchant'
                    : 'Remove Blacklisted Merchant'}
              </h3>
              <button
                type="button"
                onClick={() => setRemoveTarget(null)}
                aria-label="Close"
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#ffffff',
                  fontSize: 36,
                  lineHeight: 1,
                  cursor: 'pointer',
                  padding: 0,
                  width: 36,
                  height: 36,
                  opacity: 0.9,
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '24px 26px 18px', background: '#ffffff' }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 84,
                    height: 84,
                    borderRadius: '50%',
                    border: '3px solid #a78bfa',
                    background: '#ede9fe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 38,
                    flexShrink: 0,
                  }}
                >
                  🔒
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: '0 0 14px', color: '#111827', fontSize: 15, fontWeight: 700 }}>
                    Are you sure you want to{' '}
                    {removeTarget.type === 'blacklisted'
                      ? 'remove this blacklist entry?'
                      : removeTarget.type === 'banned'
                        ? 'unban this merchant?'
                        : 'remove this blacklisted merchant?'}
                  </p>
                  <div
                    style={{
                      borderRadius: 12,
                      border: '2px solid #8b5cf6',
                      background: '#f5f3ff',
                      padding: '14px 16px',
                      color: '#1f2937',
                    }}
                  >
                    <div style={{ fontSize: 13, marginBottom: 6 }}>
                      <b>Entry:</b> {removeTarget.label}
                    </div>
                    <div style={{ fontSize: 13 }}>
                      <b>Action:</b>{' '}
                      {removeTarget.type === 'blacklisted'
                        ? 'This payout entry will be removed from blacklist.'
                        : removeTarget.type === 'banned'
                          ? 'This merchant will be removed from banned list.'
                          : 'This merchant will be removed from blacklisted list.'}
                    </div>
                  </div>
                  <div
                    style={{
                      marginTop: 14,
                      borderRadius: 12,
                      border: '2px solid #f59e0b',
                      background: '#ede9fe',
                      color: '#312e81',
                      fontSize: 14,
                      padding: '12px 14px',
                    }}
                  >
                    This action can impact fraud controls. Please confirm carefully.
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                padding: '18px 26px 22px',
                background: '#f3f4f6',
                borderTop: '1px solid #ddd6fe',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
              }}
            >
              <button
                type="button"
                className="filter-button"
                onClick={() => setRemoveTarget(null)}
                style={{
                  minWidth: 112,
                  height: 44,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #e5e7eb, #d1d5db)',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  fontWeight: 800,
                  letterSpacing: '0.4px',
                  fontSize: 13,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="filter-button"
                onClick={confirmRemove}
                style={{
                  minWidth: 190,
                  height: 46,
                  borderRadius: 12,
                  fontWeight: 800,
                  fontSize: 14,
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  border: '1px solid rgba(255,255,255,0.35)',
                  color: '#ffffff',
                }}
              >
                {removeTarget.type === 'blacklisted'
                  ? 'REMOVE ENTRY'
                  : removeTarget.type === 'banned'
                    ? 'UNBAN MERCHANT'
                    : 'UNBLACKLIST MERCHANT'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FraudManagement;

